import { useMemo } from "react";
import { Space, Table, Empty, Tooltip, message } from "antd";
import {
  ClockCircleOutlined,
  MailOutlined,
  UserOutlined,
  CalendarOutlined,
  HistoryOutlined,
  BarChartOutlined,
  LineChartOutlined,
  CopyOutlined,
} from "@ant-design/icons";
import { Typography } from "antd";
import {
  checkIfNumber,
  formatNumber,
  getLastWeekEndingDate,
  getWeekEndingDate,
} from "../../../utils/formatters";
import { colors } from "../../../config/constants";

/**
 * Displays contributor metrics with Current Week, Last Week, and Lifetime hours columns.
 * Data comes from the contributor hours summary API.
 */
function ContributorSummaryView({ data = [], loading = false }) {
  const { Text } = Typography;
  const currentWeekEndingDate = useMemo(() => getWeekEndingDate(), []);

  const columns = useMemo(
    () => [
      {
        title: (
          <Space>
            <MailOutlined style={{ color: colors.baseBlue }} /> Contributor
            Email
          </Space>
        ),
        dataIndex: "email",
        key: "email",
        width: 200,
        ellipsis: true,
        className: "copy-icon-parent",
        render: (t, row) => (
          <div
            style={{
              width: "100%",
              display: "flex",
              justifyContent: "space-between",
            }}
          >
            <Text
              title={row.email}
              style={{
                width: "calc(100% - 1px)",
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
                display: "inline-block",
              }}
            >
              {t || "—"}
            </Text>
            <Tooltip title="Copy Email">
              <CopyOutlined
                onClick={() => {
                  navigator.clipboard.writeText(row.email);
                  message.success("Email copied to clipboard");
                }}
                style={{
                  cursor: "pointer",
                  color: colors.cyan7,
                  opacity: 0,
                }}
                className="copy-icon-show"
              />
            </Tooltip>
          </div>
        ),
        sorter: (a, b) => (a.email ?? "").localeCompare(b.email ?? ""),
      },
      {
        title: (
          <Space>
            <UserOutlined style={{ color: "#52c41a" }} /> Contributor Name
          </Space>
        ),
        dataIndex: "contributor_name",
        key: "contributor_name",
        width: 200,
        ellipsis: true,
        className: "copy-icon-parent",
        render: (t, row) => (
          <div
            style={{
              width: "100%",
              display: "flex",
              justifyContent: "space-between",
            }}
          >
            <Text
              title={row.contributor_name}
              style={{
                width: "calc(100%)",
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
                display: "inline-block",
              }}
            >
              {t || "—"}
            </Text>
            <Tooltip title="Copy Name">
              <CopyOutlined
                onClick={() => {
                  navigator.clipboard.writeText(row.contributor_name);
                  message.success("Name copied to clipboard");
                }}
                style={{
                  cursor: "pointer",
                  color: colors.cyan7,
                  opacity: 0,
                }}
                className="copy-icon-show"
              />
            </Tooltip>
          </div>
        ),
        sorter: (a, b) =>
          (a.contributor_name ?? "").localeCompare(b.contributor_name ?? ""),
      },
      {
        title: (
          <Space>
            <CalendarOutlined style={{ color: "#1890ff" }} />
            Current Week
          </Space>
        ),
        children: [
          {
            title: (
              <Space>
                <ClockCircleOutlined style={{ color: "#1890ff" }} />
                Hours
              </Space>
            ),
            dataIndex: "cp_current_week_hours",
            key: "cp_current_week_hours",
            width: 100,
            align: "right",
            sorter: (a, b) =>
              (a.week_ending_date === currentWeekEndingDate
                ? (Number(a.cp_current_week_hours) ?? 0)
                : -1) -
              (b.week_ending_date === currentWeekEndingDate
                ? (Number(b.cp_current_week_hours) ?? 0)
                : -1),
            render: (v, row) =>
              row.week_ending_date === currentWeekEndingDate &&
              checkIfNumber(v) ? (
                <Space>
                  <Text style={{ fontSize: 15 }}>{formatNumber(v)}</Text>
                  <Text type="secondary" style={{ fontSize: 12 }}>
                    hrs
                  </Text>
                </Space>
              ) : (
                "—"
              ),
          },
          {
            title: (
              <Space>
                <BarChartOutlined style={{ color: "#fa8c16" }} />
                Units
              </Space>
            ),
            dataIndex: "cp_current_week_units",
            key: "cp_current_week_units",
            width: 100,
            align: "right",
            sorter: (a, b) =>
              (a.week_ending_date === currentWeekEndingDate
                ? (Number(a.cp_current_week_units) ?? 0)
                : -1) -
              (b.week_ending_date === currentWeekEndingDate
                ? (Number(b.cp_current_week_units) ?? 0)
                : -1),
            render: (v, row) =>
              row.week_ending_date === currentWeekEndingDate && checkIfNumber(v)
                ? `${formatNumber(v)}`
                : "—",
          },
          {
            title: (
              <Space>
                <LineChartOutlined style={{ color: "#13c2c2" }} />
                Avg RPH
              </Space>
            ),
            dataIndex: "cp_current_week_avg_rph",
            key: "cp_current_week_avg_rph",
            width: 100,
            align: "right",
            sorter: (a, b) =>
              (a.week_ending_date === currentWeekEndingDate
                ? (Number(a.cp_current_week_avg_rph) ?? 0)
                : -1) -
              (b.week_ending_date === currentWeekEndingDate
                ? (Number(b.cp_current_week_avg_rph) ?? 0)
                : -1),
            render: (v, row) =>
              row.week_ending_date === currentWeekEndingDate && checkIfNumber(v)
                ? `${formatNumber(v)}`
                : "—",
          },
        ],
      },
      {
        title: (
          <Space>
            <HistoryOutlined style={{ color: "#722ed1" }} /> Last Week
          </Space>
        ),
        children: [
          {
            title: (
              <Space>
                <ClockCircleOutlined style={{ color: "#1890ff" }} />
                Hours
              </Space>
            ),
            dataIndex: "cp_previous_week_hours",
            key: "cp_previous_week_hours",
            width: 100,
            align: "right",
            sorter: (a, b) =>
              (a.week_ending_date === currentWeekEndingDate
                ? (Number(a.cp_previous_week_hours) ?? 0)
                : -1) -
              (b.week_ending_date === currentWeekEndingDate
                ? (Number(b.cp_previous_week_hours) ?? 0)
                : -1),
            render: (v, row) =>
              row.week_ending_date === currentWeekEndingDate ? (
                checkIfNumber(v) ? (
                  <Space>
                    <Text style={{ fontSize: 15 }}>{formatNumber(v)}</Text>
                    <Text type="secondary" style={{ fontSize: 12 }}>
                      hrs
                    </Text>
                  </Space>
                ) : (
                  "-"
                )
              ) : row.week_ending_date === getLastWeekEndingDate() ? (
                <Space>
                  <Text style={{ fontSize: 15 }}>
                    {formatNumber(row.cp_current_week_hours)}
                  </Text>
                  <Text type="secondary" style={{ fontSize: 12 }}>
                    hrs
                  </Text>
                </Space>
              ) : (
                "—"
              ),
          },
          {
            title: (
              <Space>
                <BarChartOutlined style={{ color: "#fa8c16" }} />
                Units
              </Space>
            ),
            dataIndex: "cp_previous_week_units",
            key: "cp_previous_week_units",
            width: 100,
            align: "right",
            sorter: (a, b) =>
              (a.week_ending_date === currentWeekEndingDate
                ? (Number(a.cp_previous_week_units) ?? 0)
                : -1) -
              (b.week_ending_date === currentWeekEndingDate
                ? (Number(b.cp_previous_week_units) ?? 0)
                : -1),
            render: (v, row) =>
              row.week_ending_date === currentWeekEndingDate
                ? checkIfNumber(v)
                  ? `${formatNumber(v)}`
                  : "—"
                : row.week_ending_date === getLastWeekEndingDate()
                  ? `${formatNumber(row.cp_current_week_units)}`
                  : "—",
          },
          {
            title: (
              <Space>
                <LineChartOutlined style={{ color: "#13c2c2" }} />
                Avg RPH
              </Space>
            ),
            dataIndex: "cp_previous_week_avg_rph",
            key: "cp_previous_week_avg_rph",
            width: 100,
            align: "right",
            sorter: (a, b) =>
              (a.week_ending_date === currentWeekEndingDate
                ? (Number(a.cp_previous_week_avg_rph) ?? 0)
                : -1) -
              (b.week_ending_date === currentWeekEndingDate
                ? (Number(b.cp_previous_week_avg_rph) ?? 0)
                : -1),
            render: (v, row) =>
              row.week_ending_date === currentWeekEndingDate
                ? checkIfNumber(v)
                  ? `${formatNumber(v)}`
                  : "—"
                : row.week_ending_date === getLastWeekEndingDate()
                  ? `${formatNumber(row.cp_current_week_avg_rph)}`
                  : "—",
          },
        ],
      },
      {
        title: (
          <Space>
            <HistoryOutlined style={{ color: "#722ed1" }} /> Lifetime
          </Space>
        ),
        children: [
          {
            title: (
              <Space>
                <ClockCircleOutlined style={{ color: "#1890ff" }} />
                Hours
              </Space>
            ),
            dataIndex: "cp_lifetime_hours",
            key: "cp_lifetime_hours",
            width: 100,
            align: "right",
            sorter: (a, b) =>
              (checkIfNumber(a.cp_lifetime_hours) ? a.cp_lifetime_hours : -1) -
              (checkIfNumber(b.cp_lifetime_hours) ? b.cp_lifetime_hours : -1),
            render: (v) =>
              checkIfNumber(v) ? (
                <Space>
                  <Text style={{ fontSize: 15 }}>{formatNumber(v)}</Text>
                  <Text type="secondary" style={{ fontSize: 12 }}>
                    hrs
                  </Text>
                </Space>
              ) : (
                "—"
              ),
          },
          {
            title: (
              <Space>
                <BarChartOutlined style={{ color: "#fa8c16" }} />
                Units
              </Space>
            ),
            dataIndex: "cp_lifetime_units",
            key: "cp_lifetime_units",
            width: 100,
            align: "right",
            sorter: (a, b) =>
              (checkIfNumber(a.cp_lifetime_units) ? a.cp_lifetime_units : -1) -
              (checkIfNumber(b.cp_lifetime_units) ? b.cp_lifetime_units : -1),
            render: (v) => (checkIfNumber(v) ? `${formatNumber(v)}` : "—"),
          },
          {
            title: (
              <Space>
                <LineChartOutlined style={{ color: "#13c2c2" }} />
                Avg RPH
              </Space>
            ),
            dataIndex: "cp_lifetime_avg_rph",
            key: "cp_lifetime_avg_rph",
            width: 100,
            align: "right",
            sorter: (a, b) =>
              (checkIfNumber(a.cp_lifetime_avg_rph)
                ? a.cp_lifetime_avg_rph
                : -1) -
              (checkIfNumber(b.cp_lifetime_avg_rph)
                ? b.cp_lifetime_avg_rph
                : -1),
            render: (v) => (checkIfNumber(v) ? `${formatNumber(v)}` : "—"),
          },
        ],
      },
    ],
    [currentWeekEndingDate],
  );

  return (
    <>
      <style>{`
        .copy-icon-parent:hover .copy-icon-show {
        opacity: 1 !important;
        transition: opacity 0.2s ease-in-out;
        }
      `}</style>
      <Table
        loading={loading}
        columns={columns}
        dataSource={data}
        bordered
        rowKey={(r) => r.contributor_id ?? String(Math.random())}
        size="small"
        pagination={{
          showSizeChanger: true,
          pageSizeOptions: ["10", "20", "30", "50"],
          showTotal: (total, range) =>
            `${range[0]}-${range[1]} of ${total} contributor${total !== 1 ? "s" : ""}`,
        }}
        scroll={{ x: 700, y: 390 }}
        locale={{
          emptyText: (
            <Empty
              description="No contributor hours summary for this scope."
              image={Empty.PRESENTED_IMAGE_SIMPLE}
            />
          ),
        }}
        rowClassName={(_, index) =>
          index % 2 === 0 ? "table-row-light" : "table-row-dark"
        }
      ></Table>
    </>
  );
}

export default ContributorSummaryView;
